import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, SimpleGrid, Heading, Text, HStack, VStack, Tag, Badge, Avatar, Progress,
  Flex, Button, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, Input,
  Select, Textarea, useToast, Spinner, IconButton, AvatarGroup, Tooltip,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { api } from "../../lib/api";

const DEPT_STATUS_COLORS = {
  not_started: "gray", in_progress: "blue", waiting_approval: "purple",
  blocked: "red", delivered: "green",
};
const PRIORITIES = ["low", "normal", "high", "urgent"];

export default function DepartmentCards({ projectId, canManage }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [depts, setDepts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const assignModal = useDisclosure();
  const empty = { team: "", poc: "", priority: "normal", deadline: "", brief: "" };
  const [f, setF] = useState(empty);

  async function load() {
    setLoading(true);
    try {
      const [d, t, u] = await Promise.all([
        api(`/projects/${projectId}/departments`),
        api("/teams").catch(() => ({ teams: [] })),
        api("/users/lookup").catch(() => ({ users: [] })),
      ]);
      setDepts(d.departments || []);
      setTeams(t.teams || []);
      setUsers(u.users || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const usedTeamIds = new Set(depts.map((d) => String(d.team?._id)));
  const availableTeams = teams.filter((t) => !usedTeamIds.has(String(t._id)));

  async function assign() {
    try {
      const body = {
        team: f.team,
        poc: f.poc || null,
        priority: f.priority,
        deadline: f.deadline || null,
        brief: f.brief,
      };
      await api(`/projects/${projectId}/departments`, { method: "POST", body: JSON.stringify(body) });
      toast({ title: "Department assigned", status: "success" });
      assignModal.onClose();
      setF(empty);
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function unassign(deptId, teamName) {
    if (!window.confirm(`Remove ${teamName} from this project? Tasks created under this dept become orphaned.`)) return;
    try {
      await api(`/project-departments/${deptId}`, { method: "DELETE" });
      toast({ title: "Removed", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  if (loading) return <Spinner />;

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="sm">Departments ({depts.length})</Heading>
        {canManage && (
          <Button leftIcon={<AddIcon />} size="sm" colorScheme="blue" onClick={assignModal.onOpen}>
            Assign Department
          </Button>
        )}
      </HStack>

      {depts.length === 0 ? (
        <Box bg="white" p={10} rounded="md" textAlign="center" color="gray.500" border="2px dashed" borderColor="gray.200">
          <Text fontSize="sm" mb={3}>No departments assigned yet.</Text>
          {canManage && (
            <Button colorScheme="blue" leftIcon={<AddIcon />} onClick={assignModal.onOpen}>
              Assign first department
            </Button>
          )}
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {depts.map((d) => {
            const overdue = d.overdueCount || 0;
            return (
              <Box
                key={d._id}
                bg="white" p={5} rounded="md" shadow="sm"
                borderTop="4px solid" borderColor={d.team?.color || "blue.400"}
                cursor="pointer"
                _hover={{ shadow: "md", transform: "translateY(-2px)" }}
                transition="all 0.15s"
                onClick={() => navigate(`/projects/${projectId}/dept/${d._id}`)}
                position="relative"
              >
                <Flex justify="space-between" align="start" mb={3}>
                  <Box flex="1">
                    <Heading size="sm" mb={1}>{d.team?.name || "—"}</Heading>
                    <HStack spacing={2}>
                      <Badge colorScheme={DEPT_STATUS_COLORS[d.status] || "gray"}>
                        {d.status.replace("_", " ")}
                      </Badge>
                      {d.priority !== "normal" && (
                        <Badge colorScheme={d.priority === "urgent" ? "red" : d.priority === "high" ? "orange" : "gray"}>
                          {d.priority}
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                  <HStack onClick={(e) => e.stopPropagation()}>
                    <Tooltip label="Open workspace">
                      <IconButton size="xs" variant="ghost" icon={<ExternalLinkIcon />} aria-label="open"
                        onClick={() => navigate(`/projects/${projectId}/dept/${d._id}`)} />
                    </Tooltip>
                    {canManage && (
                      <Tooltip label="Remove department">
                        <IconButton size="xs" variant="ghost" colorScheme="red" icon={<DeleteIcon />} aria-label="remove"
                          onClick={() => unassign(d._id, d.team?.name)} />
                      </Tooltip>
                    )}
                  </HStack>
                </Flex>

                <HStack spacing={3} mb={3}>
                  <Avatar size="xs" name={d.poc?.username || "—"} />
                  <Box flex="1">
                    <Text fontSize="xs" color="gray.500">POC</Text>
                    <Text fontSize="sm" fontWeight={600}>{d.poc?.username || "Unassigned"}</Text>
                  </Box>
                  {d.deadline && (
                    <Box textAlign="right">
                      <Text fontSize="xs" color="gray.500">Due</Text>
                      <Text fontSize="xs" fontWeight={600} color={new Date(d.deadline) < new Date() ? "red.500" : "gray.700"}>
                        {new Date(d.deadline).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </Text>
                    </Box>
                  )}
                </HStack>

                <Box>
                  <HStack justify="space-between" mb={1} fontSize="xs">
                    <Text color="gray.600">{d.doneCount}/{d.taskCount} tasks</Text>
                    <Text color={overdue > 0 ? "red.500" : "gray.500"} fontWeight={overdue > 0 ? 600 : 400}>
                      {overdue > 0 ? `${overdue} overdue` : `${d.progress}%`}
                    </Text>
                  </HStack>
                  <Progress
                    value={d.progress} size="xs" rounded="full"
                    colorScheme={d.progress === 100 ? "green" : overdue > 0 ? "red" : "blue"}
                  />
                </Box>

                {d.members && d.members.length > 0 && (
                  <HStack mt={3}>
                    <AvatarGroup size="xs" max={4}>
                      {d.members.map((m) => <Avatar key={m._id} name={m.username} />)}
                    </AvatarGroup>
                    <Text fontSize="xs" color="gray.500">{d.members.length} members</Text>
                  </HStack>
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      <Modal isOpen={assignModal.isOpen} onClose={assignModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Department</ModalHeader><ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>Department</FormLabel>
              <Select placeholder="Pick a team" value={f.team} onChange={(e) => setF({ ...f, team: e.target.value })}>
                {availableTeams.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
                {availableTeams.length === 0 && <option disabled>All teams already assigned</option>}
              </Select>
            </FormControl>
            <SimpleGrid columns={2} spacing={3} mb={3}>
              <FormControl>
                <FormLabel>Department POC</FormLabel>
                <Select placeholder="Defaults to team lead" value={f.poc} onChange={(e) => setF({ ...f, poc: e.target.value })}>
                  {users.map((u) => <option key={u._id} value={u._id}>{u.username}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Priority</FormLabel>
                <Select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
                  {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                </Select>
              </FormControl>
            </SimpleGrid>
            <FormControl mb={3}>
              <FormLabel>Deadline</FormLabel>
              <Input type="date" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} />
            </FormControl>
            <FormControl>
              <FormLabel>Brief (department-specific)</FormLabel>
              <Textarea rows={3} value={f.brief} onChange={(e) => setF({ ...f, brief: e.target.value })}
                placeholder="What does this department need to deliver for this project?" />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={assignModal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={assign} isDisabled={!f.team}>Assign</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
